import { CellData, TabData } from '@ddt-web/data-service';
import { chain } from 'lodash';
import * as get from 'lodash/get';

import { AreaSplineChartConfig, BarChartConfig, ChartType, ColumnChartConfig, DoughnutChartConfig, EmptyChartColumnConfig, GridChartConfig, HeatmapChartConfig, IndicatorColumnChartConfig, LineChartConfig, PieChartConfig, ProgressBarConfig, ScatterChartConfig, StackedGroupedColumnChartConfigPdf } from '../config';
import { LineChartSeriesCustomisations, ScatterChartSeriesCustomisations } from '../models';

export class CommonChartUtil {

	/**
	 * recalculateAxisFromData: recalculate tickPositions property for stacked chart [min from data, sum of all data].
	 * @param {any[]} seriesData: chart series data.
	 * @param {string} axis: axis to recalculate.
	 * @param {number} padding: number of times to have set the min/max of the axis
	 */
	public static recalculateAxisFromData(seriesData: any[], axis: string = 'y', padding: number = 1): any[] {
		const dataPoints = chain(seriesData)
			.map(point => point.data)
			.flatten()
			.map(data => (get(data, axis) ? get(data, axis) : get(data, axis === 'y' ? '1' : '0', 0)))
			.flattenDeep()
			.value();
		return [
			// remove get(point.data[0],'1') after everything is refactor to have iData as x and y point
			Math.min(...dataPoints, 0) * padding,
			dataPoints.reduce((sum, data) => sum + Math.max(data, 0), 0) * padding
		];
	}

	/**
	 * findMinFromData: recalculate min property for chart.
	 * @param {any[]} seriesData: chart series data.
	 * @param {string} axis: axis to recalculate.
	 */
	public static findMinFromData(seriesData: any[], axis: string = 'y'): number {
		const dataPoints = chain(seriesData)
			.map(point => point.data)
			.flatten()
			.map(data => (get(data, axis) ? get(data, axis) : get(data, axis === 'y' ? '1' : '0', 0)))
			.flattenDeep()
			.value();
		return Math.floor(Math.min(...dataPoints, 0));
	}

	/**
	 * findMinFromData: recalculate max property for chart.
	 * @param {any[]} seriesData: chart series data.
	 * @param {string} axis: axis to recalculate.
	 */
	public static findMaxFromData(seriesData: any[], axis: string = 'y'): number {
		const dataPoints = chain(seriesData)
			.map(point => point.data)
			.flatten()
			.map(data => (get(data, axis) ? get(data, axis) : get(data, axis === 'y' ? '1' : '0', 0)))
			.flattenDeep()
			.value();
		return Math.ceil(Math.max(...dataPoints) * 1.2);
	}

	public static getChartConfigForPDF(
		type: string,
		name: string,
		data: TabData | TabData[],
		width: number,
		height: number,
		customAttributes?: any,
		portfolioIndex: number = 0
	): Array<any> {
		const chartTypeConfigs = {
			bar: BarChartConfig,
			column: ColumnChartConfig,
			pie: PieChartConfig,
			scatter: ScatterChartConfig,
			line: LineChartConfig,
			stackedGroupedColumn: StackedGroupedColumnChartConfigPdf,
			grid: GridChartConfig,
			emptyChartColumn: EmptyChartColumnConfig,
			doughnut: DoughnutChartConfig,
			indicatorColumnChart: IndicatorColumnChartConfig,
			heatmap: HeatmapChartConfig,
			progressBar: ProgressBarConfig,
			areaspline: AreaSplineChartConfig
		};

		if (type === ChartType.Pie) {
			return get(data, `tableData[${portfolioIndex}].headersX`, []).map((cell: CellData, index: number, cells: Array<CellData>) => {
				const el = document.createElement('div');
				el.id = 'chart-' + index;
				const args = [null, type, name, el, data, index, portfolioIndex, null, height, width / cells.length];
				const chart = new (Function.prototype.bind.apply(chartTypeConfigs[type], args))();
				return chart.getConfigurationForPDF();
			});
		}

		const args = [null, type, name, document.createElement('div'), data, portfolioIndex, null, height, width, null, null, null, null, customAttributes];
		const chart = new (Function.prototype.bind.apply(chartTypeConfigs[type], args))();

		return [chart.getConfigurationForPDF(customAttributes)];
	}

	/**
	 * Method to combine custom series colours and series prefixes into LineChartSeriesCustomisations which is used by the ChartFactory
	 * The number of arrays in the seriesColours must equal the number of prefixes i.e. one set of colours and one prefix per series
	 * Either param can be omitted, but if both are provided, the lengths must be equal
	 * Returns empty array if passed in falsy params or params of unequal length
	 * 
	 * @param seriesColors - array containing an array of colours for each tab
	 * @param seriesPrefixes - array containing a string prefix for each tab, can be ""
	 * @param tooltipPortfolioNameTemplate - will be used by StringUtil.fillTemplate() to fill in the portfolio name and prefix
	 */
	public static createCustomisationsArray(
		seriesColors?: Array<Array<string>>,
		tooltipPrefixes?: Array<string>,
		tooltipPortfolioNameTemplate?: string,
		tooltipPortfolioNameLength?: number
	): Array<LineChartSeriesCustomisations> | Array<ScatterChartSeriesCustomisations> {
		const noArgsProvided = !seriesColors && !tooltipPrefixes && !tooltipPortfolioNameTemplate && !tooltipPortfolioNameLength;
		const colorsPrefixesLengthMismatch = seriesColors && tooltipPrefixes && seriesColors.length !== tooltipPrefixes.length;

		if (noArgsProvided || colorsPrefixesLengthMismatch) {
			if (colorsPrefixesLengthMismatch) {
				console.warn('Mismatch when creating line chart customisations. The number of colours provided !== the number of tooltip prefixes');
			}
			return [];
		} else {
			const seriesCount = seriesColors ? seriesColors.length : tooltipPrefixes.length;
			const retVal = [];
			for (let i = 0; i < seriesCount; i++) {
				retVal.push({
					colors: seriesColors ? seriesColors[i] : null,
					prefix: tooltipPrefixes ? tooltipPrefixes[i] : null,
					tooltipPortfolioNameTemplate,
					tooltipPortfolioNameLength
				});
			}

			return retVal;
		}
	}
}
